// FIELD WORKER HOME — DESIGN PREVIEW A: "Tactical HUD" (dark theme)
// Self-contained mock. Not wired to real data.
import {
  MapPin, Navigation, Clock, ShieldAlert, ClipboardCheck, Trophy,
  FileSignature, ClipboardList, Palmtree, Home, Briefcase, Wrench,
  CircleUser, ChevronRight, Zap, HardHat,
} from "lucide-react";

const font = { fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif" };

export default function FieldPreview1() {
  return (
    <div className="min-h-screen bg-zinc-900 py-6" data-testid="field-preview-1">
      <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;800&display=swap" rel="stylesheet" />
      <div className="max-w-[390px] mx-auto min-h-[800px] relative bg-[#09090B] text-white border border-zinc-700 overflow-hidden pb-24">

        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#09090B] border-b border-white/10 px-4 pt-5 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF5F15]">/ Field cockpit</div>
              <h1 style={font} className="text-3xl font-extrabold uppercase tracking-tight leading-none mt-1">Mōrena, Ben</h1>
            </div>
            <div className="w-11 h-11 bg-zinc-900 border border-white/10 flex items-center justify-center">
              <HardHat className="w-5 h-5 text-[#FF5F15]" />
            </div>
          </div>
          {/* Geofence strip */}
          <div className="mt-3 flex items-center justify-between bg-zinc-900 border border-[#39FF14]/40 px-3 py-2" data-testid="p1-geofence">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#39FF14] animate-pulse" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-[#39FF14]">On site · Kereru Rd</span>
            </div>
            <button className="text-[10px] font-bold uppercase tracking-widest bg-[#39FF14] text-black px-3 py-1.5 active:scale-[0.98]">Sign out</button>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-3">
          {/* Today's run sheet */}
          <div className="border border-white/10 bg-zinc-900" data-testid="p1-runsheet">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
              <span style={font} className="text-sm font-extrabold uppercase tracking-wider">Today's run sheet</span>
              <span className="text-[10px] font-mono text-zinc-400">MON 28 JUL</span>
            </div>
            <div className="px-4 py-3">
              <div className="flex items-start gap-3">
                <span className="bg-[#FF5F15] text-black font-mono font-bold text-xs px-2 py-1">J0017</span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[15px] leading-tight">Stormwater upgrade — Stage 2</div>
                  <div className="flex items-center gap-1 text-xs text-zinc-400 mt-1"><MapPin className="w-3 h-3" /> 45 Kereru Rd, Papakura</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                {[["07:00", "START"], ["3/5", "TASKS"], ["2", "CREW"]].map(([v, l]) => (
                  <div key={l} className="bg-zinc-900 px-2 py-2 text-center">
                    <div className="font-mono font-bold text-lg text-white leading-none">{v}</div>
                    <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bento: hours + tomorrow */}
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-white/10 bg-zinc-900 p-4" data-testid="p1-hours">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">This week</div>
              <div className="flex items-end gap-1 mt-2">
                <span className="font-mono font-bold text-4xl leading-none text-[#FF5F15]">32.5</span>
                <span className="text-[10px] font-bold uppercase text-zinc-500 mb-0.5">hrs</span>
              </div>
              <div className="text-[10px] font-mono text-zinc-400 mt-2">24.0 approved · 8.5 pending</div>
              <div className="mt-2 h-1.5 bg-zinc-800"><div className="h-full bg-[#39FF14]" style={{ width: "74%" }} /></div>
            </div>
            <div className="border border-white/10 bg-zinc-900 p-4" data-testid="p1-tomorrow">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Tomorrow</div>
              <div style={font} className="font-extrabold uppercase text-lg leading-tight mt-1.5">Culvert install</div>
              <div className="text-[11px] text-zinc-400 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> 06:30 start</div>
              <button className="mt-3 w-full flex items-center justify-center gap-1.5 border border-[#FF5F15] text-[#FF5F15] text-[10px] font-bold uppercase tracking-widest py-2 active:scale-[0.98]">
                <Navigation className="w-3 h-3" /> Directions
              </button>
            </div>
          </div>

          {/* Primary CTA */}
          <button className="w-full h-16 bg-[#FF5F15] text-black flex items-center justify-between px-5 active:scale-[0.98] transition-transform duration-75" data-testid="p1-log-hours">
            <div className="text-left">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] opacity-70">Today</div>
              <div style={font} className="text-2xl font-extrabold uppercase tracking-tight leading-none">Log your hours</div>
            </div>
            <Zap className="w-7 h-7" strokeWidth={2.5} />
          </button>

          {/* Quick actions grid */}
          <div className="grid grid-cols-2 gap-3" data-testid="p1-actions">
            {[
              [ShieldAlert, "Report hazard", "Photo + log it now", "#FF3B30"],
              [ClipboardCheck, "QA sign-off", "Tick finished work", "#FF5F15"],
              [ClipboardList, "Forms", "Permits & checks", "#FF5F15"],
              [FileSignature, "Variations", "Extra work on site", "#FF5F15"],
              [Trophy, "Leaderboard", "Where you sit", "#39FF14"],
              [Palmtree, "Leave", "Book time off", "#39FF14"],
            ].map(([Icon, t, s, c]) => (
              <button key={t} className="border border-white/10 bg-zinc-900 p-4 text-left active:scale-[0.98] transition-transform duration-75">
                <Icon className="w-5 h-5 mb-3" style={{ color: c }} />
                <div style={font} className="font-extrabold uppercase text-sm tracking-wide leading-none">{t}</div>
                <div className="text-[10px] text-zinc-500 mt-1">{s}</div>
              </button>
            ))}
          </div>

          {/* Mini stats ticker */}
          <div className="flex items-center gap-4 border border-white/10 bg-zinc-900 px-4 py-2.5 font-mono text-[11px]" data-testid="p1-ministats">
            <span className="text-zinc-400">JOBS <b className="text-white">4</b></span>
            <span className="text-zinc-400">PENDING <b className="text-[#FF5F15]">2</b></span>
            <span className="text-zinc-400">SAFETY <b className="text-[#FF3B30]">1</b></span>
            <ChevronRight className="w-3.5 h-3.5 ml-auto text-zinc-600" />
          </div>
        </div>

        {/* Bottom nav */}
        <div className="absolute bottom-0 inset-x-0 bg-[#09090B] border-t border-white/10 grid grid-cols-6 py-2.5" data-testid="p1-bottomnav">
          {[[Home, "Home", true], [Briefcase, "Jobs"], [Clock, "Hours"], [ShieldAlert, "Safety"], [Wrench, "Toolbox"], [CircleUser, "Me"]].map(([Icon, l, active]) => (
            <div key={l} className="flex flex-col items-center gap-1">
              <Icon className={`w-5 h-5 ${active ? "text-[#FF5F15]" : "text-zinc-500"}`} />
              <span className={`text-[9px] font-bold uppercase tracking-wider ${active ? "text-[#FF5F15]" : "text-zinc-600"}`}>{l}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="text-center text-zinc-400 text-xs font-mono mt-4 uppercase tracking-widest">Preview A — Tactical HUD (dark)</div>
    </div>
  );
}
